import React, { useState } from "react";
import { Bar } from "react-chartjs-2";
import { FaHospitalAlt } from "react-icons/fa";
import { FaRibbon } from "react-icons/fa";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// Default cancer data
const DEFAULT_CANCER_DATA = {
  Oral: { Screened: 20032942, Examined: 1304593, Diagnosed: 0, Treatment: 0 },
  Breast: { Screened: 0, Examined: 680821, Diagnosed: 0, Treatment: 0 },
  Cervical: { Screened: 9346588, Examined: 1467834, Diagnosed: 0, Treatment: 476 },
};

export default function CancerScreeningCard({ selectedDistrict = null, seed = null }) {
  const [open, setOpen] = useState(false);

  // If district is selected, scale numbers to 20%
  const cancerData = selectedDistrict
    ? Object.fromEntries(
      Object.entries(DEFAULT_CANCER_DATA).map(([cancerType, vals]) => [
        cancerType,
        Object.fromEntries(Object.entries(vals).map(([k, v]) => [k, Math.round(v * 0.2)])),
      ])
    )
    : DEFAULT_CANCER_DATA;

  const labels = Object.keys(cancerData);
  const datasets = ["Screened", "Examined", "Diagnosed", "Treatment"].map((key, idx) => ({
    label: key,
    data: labels.map((c) => cancerData[c][key]),
    backgroundColor: ["#6366f1", "#06b6d4", "#ef4444", "#f97316"][idx],
  }));

  const data = { labels, datasets };
  const options = {
    responsive: true,
    plugins: { legend: { position: "bottom" },  datalabels: false },
    scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true } },
  };

  const totalScreened = labels.reduce((sum, l) => sum + cancerData[l].Screened, 0);
  // Dummy last month number (reduce by 10% just for comparison demo)
  const lastMonth = Math.round(totalScreened * 0.9);

  // Compare
  const diff = totalScreened - lastMonth;
  const percent = Math.round((diff / lastMonth) * 100);

  // Always show in red → as per your request
  const isUp = diff > 0; // Optional, though color stays red
  return (
    <>
      {/* Small Card */}
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-3 px-3 py-2 rounded-lg shadow-sm bg-white border hover:shadow-md w-full"
      >
        <div className="w-10 h-10 flex items-center justify-center rounded-md bg-purple-100">
          <FaRibbon className="text-purple-600 w-5 h-5" />
        </div>
        <div className="text-left">
          <div className="text-xs text-slate-600 font-medium">Cancer Screened</div>
          <div className="text-lg font-semibold">{totalScreened.toLocaleString('en-IN')}</div>
          <div className="text-[11px]">
            <span className="text-red-600">
              {isUp ? "▼" : "▲"} {Math.abs(percent)}%
            </span>
            <span className="text-slate-500"> vs previous period</span>
          </div>
        </div>
      </button>

      {/* Offcanvas */}
      {open && (
        <div className="fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="relative ml-auto w-full max-w-2xl h-full bg-white shadow-2xl overflow-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Cancer Screening Details</h3>
              <button onClick={() => setOpen(false)} className="px-3 py-1 rounded bg-slate-100 text-sm">
                Close
              </button>
            </div>

            <div className="p-4">
              <Bar data={data} options={options} />
              <div className="mt-4 text-xs text-slate-500">
                Source: HMIS-Apr24 to Mar 25
              </div>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
